/**
 * Checklist Mutations Hook
 * Handles creating, updating, deleting, and reordering checklists
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface CreateChecklistInput {
  serviceId: number;
  name: string;
  description?: string;
  wall_by_wall?: boolean;
}

interface UpdateChecklistInput {
  id: number;
  name: string;
  description?: string;
  wall_by_wall?: boolean;
}

interface ReorderChecklistsInput {
  serviceId: number;
  checklistIds: number[];
}

export const useChecklistMutations = (serviceId?: number) => {
  const queryClient = useQueryClient();

  const createChecklistMutation = useMutation({
    mutationFn: async (input: CreateChecklistInput) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: input.serviceId,
          name: input.name,
          description: input.description || "",
          wall_by_wall: input.wall_by_wall,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create checklist");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-checklists", serviceId] });
      toast.success("Checklist created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create checklist: ${error.message}`);
    },
  });

  const updateChecklistMutation = useMutation({
    mutationFn: async (input: UpdateChecklistInput) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/${input.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          description: input.description || "",
          wall_by_wall: input.wall_by_wall !== undefined ? input.wall_by_wall : false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update checklist");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-checklists", serviceId] });
      toast.success("Checklist updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update checklist: ${error.message}`);
    },
  });

  const deleteChecklistMutation = useMutation({
    mutationFn: async (checklistId: number) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/${checklistId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete checklist");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-checklists", serviceId] });
      toast.success("Checklist deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete checklist: ${error.message}`);
    },
  });

  const reorderChecklistsMutation = useMutation({
    mutationFn: async (input: ReorderChecklistsInput) => {
      const response = await fetch(
        `${apiUrl}/api/v1/services/${input.serviceId}/checklists/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            checklist_ids: input.checklistIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to reorder checklists");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-checklists", serviceId] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to reorder checklists: ${error.message}`);
    },
  });

  return {
    createChecklistMutation,
    updateChecklistMutation,
    deleteChecklistMutation,
    reorderChecklistsMutation,
    isCreating: createChecklistMutation.isPending,
    isUpdating: updateChecklistMutation.isPending,
    isDeleting: deleteChecklistMutation.isPending,
    isReordering: reorderChecklistsMutation.isPending,
  };
};
